import React, { useMemo, useState } from 'react';
import { 
  X, BarChart2, PieChart, Clock, Users, Activity, Target, 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, 
  Calendar, DollarSign, Zap, Award, Timer, Percent, Gauge,
  ThumbsUp, Lightbulb, Bell, PlayCircle
} from 'lucide-react';
// @ts-ignore
import { Action, Organization } from '../types';
import { isActionOverdue, parseActionDeadline } from '../utils/actionDeadline';

interface ActionDashboardProps {
  actions: Action[];
  projects?: Array<{
    id: string;
    nome_projeto: string;
    data_criacao: string;
    ultimo_salvamento: string;
  }>;
  organization?: Organization | null;
  sectors?: string[];
  isOpen?: boolean;
  onClose?: () => void;
}

// ============================================================================
// COMPONENTES DE UI REUTILIZÁVEIS
// ============================================================================

const KPICard: React.FC<{ 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon?: React.ReactNode; 
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  colorClass?: string;
}> = ({ title, value, subtitle, icon, trend, trendValue, colorClass = 'border border-slate-300 bg-slate-100 text-slate-600' }) => (
    <div className="enterprise-card-surface border border-enterprise-border-soft p-2.5">
    <div className="mb-2 flex items-start justify-between">
      <div className={`flex h-8 w-8 items-center justify-center ${colorClass}`}>
        {icon}
      </div>
      {trend && trendValue && (
        <div className={`flex items-center gap-1 text-[10px] font-medium ${
          trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : trend === 'down' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'
        }`}>
          {trend === 'up' ? <TrendingUp size={12} /> : trend === 'down' ? <TrendingDown size={12} /> : null}
          <span>{trendValue}</span>
        </div>
      )}
    </div>
    <div className="space-y-0.5">
      <div className="text-xl font-semibold text-slate-800 dark:text-slate-100">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">{title}</div>
      {subtitle && <div className="text-[10px] text-slate-400 dark:text-slate-500">{subtitle}</div>}
    </div>
  </div>
);

const InsightCard: React.FC<{
  type: 'danger' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  icon: React.ReactNode;
}> = ({ type, title, message, icon }) => {
  const colors = {
    danger: 'bg-slate-100 dark:bg-[var(--bg-surface)] border-slate-300 dark:border-slate-700',
    warning: 'bg-slate-100 dark:bg-[var(--bg-surface)] border-slate-300 dark:border-slate-700',
    success: 'bg-slate-100 dark:bg-[var(--bg-surface)] border-slate-300 dark:border-slate-700',
    info: 'bg-slate-100 dark:bg-[var(--bg-surface)] border-slate-300 dark:border-slate-700'
  };
  
  return (
    <div className={`border p-2.5 ${colors[type]}`}>
      <div className="flex gap-3">
        <div className="shrink-0 mt-0.5">{icon}</div>
        <div className="space-y-1">
          <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-200">{title}</div>
          <div className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">{message}</div>
        </div>
      </div>
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  'Concluída':    '#22c55e',
  'Em Andamento': '#3b82f6',
  'A Fazer':      '#94a3b8',
  'Cancelada':    '#f87171',
  'Atrasada':     '#ef4444',
};

const SimpleBarChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  // Same viewBox as ScatterPlot (320×180) so both SVGs render at identical height via w-full h-auto.
  const vW = 320; const vH = 180;
  const padX = 10; const padTop = 8; const labelH = 18;
  const plotH = vH - padTop - labelH; // bar area height in SVG units
  const keys = Object.keys(data);
  const max = Math.max(...Object.values(data), 1);
  const colW = (vW - padX * 2) / keys.length;
  return (
    <svg viewBox={`0 0 ${vW} ${vH}`} className="w-full h-auto" style={{ display: 'block' }}>
      {/* Baseline */}
      <line x1={padX} y1={padTop + plotH} x2={vW - padX} y2={padTop + plotH} stroke="#e2e8f0" strokeWidth="1" />
      {keys.map((k, i) => {
        const barH = data[k] > 0 ? Math.max((data[k] / max) * plotH, 4) : 0;
        const x = padX + i * colW + colW * 0.1;
        const w = colW * 0.8;
        const y = padTop + plotH - barH;
        return (
          <g key={k}>
            {barH > 0 && (
              <rect x={x} y={y} width={w} height={barH} fill={STATUS_COLORS[k] ?? '#64748b'} rx={2} />
            )}
            <text x={x + w / 2} y={vH - 4} textAnchor="middle" fontSize="9" fill="#94a3b8">{k}</text>
          </g>
        );
      })}
    </svg>
  );
};

const ScatterPlot: React.FC<{ points: { x: number; y: number; label?: string }[] }> = ({ points }) => {
  const vW = 320; const vH = 180; const pad = 28;
  const plotW = vW - pad * 2; const plotH = vH - pad * 2;
  return (
    <svg
      viewBox={`0 0 ${vW} ${vH}`}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-auto border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-[var(--bg-surface)]"
      style={{ display: 'block' }}
    >
      {/* Quadrant backgrounds */}
      <rect x={pad} y={pad} width={plotW / 2} height={plotH / 2} fill="#f0fdf4" opacity="0.7" />
      <rect x={pad + plotW / 2} y={pad} width={plotW / 2} height={plotH / 2} fill="#eff6ff" opacity="0.7" />
      <rect x={pad} y={pad + plotH / 2} width={plotW / 2} height={plotH / 2} fill="#fefce8" opacity="0.7" />
      <rect x={pad + plotW / 2} y={pad + plotH / 2} width={plotW / 2} height={plotH / 2} fill="#fff1f2" opacity="0.5" />
      {/* Grid lines */}
      <g stroke="#e2e8f0" strokeWidth="0.5">
        {[1,2,3].map(i => (
          <line key={`gy${i}`} x1={pad} y1={pad + (plotH / 4) * i} x2={pad + plotW} y2={pad + (plotH / 4) * i} />
        ))}
        {[1,2,3].map(i => (
          <line key={`gx${i}`} x1={pad + (plotW / 4) * i} y1={pad} x2={pad + (plotW / 4) * i} y2={pad + plotH} />
        ))}
      </g>
      {/* Border axes */}
      <g stroke="#94a3b8" strokeWidth="1">
        <line x1={pad} y1={pad + plotH} x2={pad + plotW} y2={pad + plotH} />
        <line x1={pad} y1={pad} x2={pad} y2={pad + plotH} />
      </g>
      {/* Center cross (dashed) — at 5.5/10 = 50% of 1-10 scale */}
      <g stroke="#94a3b8" strokeWidth="0.75" strokeDasharray="3,3">
        <line x1={pad} y1={pad + plotH / 2} x2={pad + plotW} y2={pad + plotH / 2} />
        <line x1={pad + plotW / 2} y1={pad} x2={pad + plotW / 2} y2={pad + plotH} />
      </g>
      {/* Axis labels */}
      <text x={pad + plotW / 2} y={vH - 5} textAnchor="middle" fontSize="8" fill="#94a3b8">Esforço →</text>
      <text x={9} y={pad + plotH / 2} textAnchor="middle" fontSize="8" fill="#94a3b8" transform={`rotate(-90,9,${pad + plotH / 2})`}>Impacto →</text>
      {/* Points — 0-100 domain, quadrant centre at 50 */}
      {points.map((p, i) => {
        const cx = pad + (p.x / 100) * plotW;
        const cy = pad + plotH - (p.y / 100) * plotH;
        return <circle key={i} cx={cx} cy={cy} r={4} fill="#475569" opacity={0.85} />;
      })}
      {points.length === 0 && (
        <text x={vW / 2} y={vH / 2 + 4} textAnchor="middle" fontSize="10" fill="#94a3b8">Sem dados</text>
      )}
    </svg>
  );
};

// ─── Gantt helpers ──────────────────────────────────────────────────────────

const MS_DAY = 86_400_000;

function fmtDateDDMMYYYY(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function ganttBarColor(status: string, endMs: number, nowMs: number): string {
  if (status === 'done')     return '#10b981'; // emerald-500
  if (status === 'canceled') return '#94a3b8'; // slate-400
  if (endMs < nowMs)         return '#ef4444'; // red-500  — overdue
  if (status === 'doing')    return '#3b82f6'; // blue-500
  return '#f59e0b';                            // amber-400 — todo, not yet overdue
}

type GanttItem = {
  id: string;
  title: string;
  status: string;
  start: number;
  end: number;
  responsible: string;
};

const GanttView: React.FC<{ actions: Action[] }> = ({ actions }) => {
  const [hoveredId, setHoveredId]     = useState<string | null>(null);
  const [tooltipPos, setTooltipPos]   = useState({ x: 0, y: 0 });
  const nowMs = Date.now();

  const items = useMemo<GanttItem[]>(() => {
    return actions
      .filter(a => !!a.title)
      .map(a => {
        const rawEnd = (a as any).deadline ?? (a as any).prazo;
        const endMs  = rawEnd ? new Date(rawEnd).getTime() : NaN;
        // If the action has a deadline: anchor the visual start to 3 days before the
        // deadline so the bar appears as a compact block near its due date rather than
        // spanning from the record creation date all the way to the deadline.
        // If no deadline at all: fall back to a 7-day bar from today.
        const start = !isNaN(endMs)
          ? endMs - 3 * MS_DAY
          : (a.createdAt ? new Date(a.createdAt).getTime() : nowMs - 7 * MS_DAY);
        const end   = !isNaN(endMs) ? endMs : start + 7 * MS_DAY;
        return {
          id: a.id,
          title: a.title,
          status: a.status ?? 'todo',
          start,
          end: Math.max(end, start + MS_DAY),
          responsible: (a as any).responsavel ?? (a as any).responsible ?? '—',
        };
      })
      .sort((x, y) => x.start - y.start);
  }, [actions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
        Nenhuma ação com data definida.
      </div>
    );
  }

  // ── Ruler range: snap left to Monday before earliest start ──────────────
  const rawMin = Math.min(...items.map(i => i.start));
  // Ensure the axis always extends at least 30 days past today, or to the
  // furthest deadline — whichever is greater.
  const rawMax = Math.max(...items.map(i => i.end), nowMs + 30 * MS_DAY);

  const axisStart = new Date(rawMin);
  const dow = axisStart.getDay(); // 0=Sun
  axisStart.setDate(axisStart.getDate() - (dow === 0 ? 6 : dow - 1)); // back to Monday
  axisStart.setHours(0, 0, 0, 0);

  // Build week tick array (each Monday until past rawMax + 1 week)
  const weekTicks: Date[] = [];
  const cursor = new Date(axisStart);
  while (cursor.getTime() <= rawMax + 7 * MS_DAY) {
    weekTicks.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }

  const axisEnd   = weekTicks[weekTicks.length - 1].getTime();
  const axisRange = axisEnd - axisStart.getTime() || MS_DAY;

  const toPct = (ms: number) =>
    ((ms - axisStart.getTime()) / axisRange) * 100;

  const widthPct = (s: number, e: number) =>
    Math.max(((e - s) / axisRange) * 100, 0.6);

  const todayPct   = toPct(nowMs);
  const hoveredItem = hoveredId ? items.find(i => i.id === hoveredId) : null;

  const LABEL_W  = 148; // px — sticky column width
  // Give each week tick ~72px so the grid is wide enough to scroll properly
  const MIN_GRID_W = Math.max(weekTicks.length * 72, 480);

  return (
    <div className="relative select-none text-[11px]">

      {/* ── Outer flex: sticky label | scrollable grid ─────────────────── */}
      <div className="flex">

        {/* ── Sticky label column ────────────────────────────────────────── */}
        <div
          className="sticky left-0 z-10 shrink-0 border-r border-slate-100 bg-white dark:border-slate-700 dark:bg-[var(--bg-surface)]"
          style={{ width: LABEL_W, minWidth: LABEL_W }}
        >
          {/* Ruler-row spacer (matches ruler height) */}
          <div className="h-5 border-b border-slate-200 dark:border-slate-700" />
          {/* One label cell per action row */}
          <div className="mt-1 space-y-[3px]">
            {items.slice(0, 30).map(item => (
              <div
                key={item.id}
                className="flex h-[18px] items-center truncate pr-2.5 text-[11px] leading-none text-slate-600 dark:text-slate-400"
                title={item.title}
              >
                {item.title}
              </div>
            ))}
          </div>
        </div>

        {/* ── Scrollable grid ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-x-auto">
          <div className="relative" style={{ minWidth: MIN_GRID_W }}>

            {/* Ruler header */}
            <div className="relative h-5 border-b border-slate-200 dark:border-slate-700">
              {weekTicks.map((w, idx) => {
                const lp = toPct(w.getTime());
                return (
                  <div
                    key={idx}
                    className="absolute top-0 flex h-full flex-col justify-end"
                    style={{ left: `${lp}%` }}
                  >
                    <span className="border-l border-slate-200 pl-1 text-[9px] font-medium leading-none text-slate-400 dark:border-slate-700 dark:text-slate-500">
                      {String(w.getDate()).padStart(2, '0')}/{String(w.getMonth() + 1).padStart(2, '0')}
                    </span>
                  </div>
                );
              })}
              {/* Today marker on ruler */}
              {todayPct >= 0 && todayPct <= 100 && (
                <div
                  className="absolute top-0 h-full border-l-2 border-dashed border-blue-400"
                  style={{ left: `${todayPct}%` }}
                />
              )}
            </div>

            {/* Rows */}
            <div className="mt-1 space-y-[3px]">
              {items.slice(0, 30).map(item => {
                // Crop bars that start before the axis left edge
                const rawLeft  = toPct(item.start);
                const rawWidth = widthPct(item.start, item.end);
                const left  = Math.max(0, rawLeft);
                const width = rawLeft < 0 ? Math.max(rawWidth + rawLeft, 0.6) : rawWidth;
                const color = ganttBarColor(item.status, item.end, nowMs);

                return (
                  <div key={item.id} className="relative h-[18px] bg-slate-100 dark:bg-slate-800/60">
                    {/* Week grid lines */}
                    {weekTicks.map((w, idx) => (
                      <div
                        key={idx}
                        className="absolute inset-y-0 border-l border-slate-200 dark:border-slate-700"
                        style={{ left: `${toPct(w.getTime())}%` }}
                      />
                    ))}
                    {/* Today line */}
                    {todayPct >= 0 && todayPct <= 100 && (
                      <div
                        className="absolute inset-y-0 border-l-2 border-dashed border-blue-300 opacity-60"
                        style={{ left: `${todayPct}%` }}
                      />
                    )}
                    {/* Bar — extends to real deadline, not clipped at today */}
                    <div
                      className="absolute top-[3px] h-3 cursor-pointer rounded-[2px] transition-opacity hover:opacity-100"
                      style={{ left: `${left}%`, width: `${width}%`, backgroundColor: color, opacity: 0.82 }}
                      onMouseEnter={e => { setHoveredId(item.id); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                      onMouseMove={e => setTooltipPos({ x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setHoveredId(null)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
        {([
          ['#10b981', 'Concluída'],
          ['#3b82f6', 'Em Andamento'],
          ['#f59e0b', 'A Fazer'],
          ['#ef4444', 'Atrasada'],
          ['#94a3b8', 'Cancelada'],
        ] as [string, string][]).map(([c, lbl]) => (
          <span key={lbl} className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c }} />
            {lbl}
          </span>
        ))}
        <span className="ml-1 flex items-center gap-1">
          <span className="inline-block h-3 border-l-2 border-dashed border-blue-400" />
          Hoje
        </span>
      </div>

      {/* ── Tooltip ────────────────────────────────────────────────────── */}
      {hoveredItem && (
        <div
          className="pointer-events-none fixed z-[9999] min-w-[190px] rounded border border-slate-200 bg-white px-3 py-2.5 shadow-lg dark:border-slate-700 dark:bg-slate-800"
          style={{ top: tooltipPos.y - 8, left: tooltipPos.x + 14 }}
        >
          <p className="mb-1.5 text-[11px] font-semibold leading-snug text-slate-700 dark:text-slate-100">
            {hoveredItem.title}
          </p>
          <div className="space-y-0.5 text-[10px] text-slate-500 dark:text-slate-400">
            <div><span className="font-medium text-slate-600 dark:text-slate-300">Responsável:</span> {hoveredItem.responsible}</div>
            <div><span className="font-medium text-slate-600 dark:text-slate-300">Início:</span> {fmtDateDDMMYYYY(hoveredItem.start)}</div>
            <div><span className="font-medium text-slate-600 dark:text-slate-300">Prazo:</span> {fmtDateDDMMYYYY(hoveredItem.end)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionDashboard: React.FC<ActionDashboardProps> = ({ 
  actions, 
  projects = [],
  organization,
  sectors = [], 
  isOpen = false, 
  onClose 
}) => {
  const [sectorFilter, setSectorFilter] = useState<string>('Todos');

  // ============================================================================
  // CÁLCULOS E MÉTRICAS
  // ============================================================================
  
  const filtered = useMemo(() => (
    sectorFilter === 'Todos' ? actions : actions.filter(a => (a.cardData?.setor || 'Geral') === sectorFilter)
  ), [actions, sectorFilter]);

  const lateActions = useMemo(() => (
    filtered
      .filter((action) => isActionOverdue(action))
      .sort((left, right) => {
        const leftDue = parseActionDeadline(left.prazo)?.getTime() ?? parseActionDeadline(left.deadline)?.getTime() ?? 0;
        const rightDue = parseActionDeadline(right.prazo)?.getTime() ?? parseActionDeadline(right.deadline)?.getTime() ?? 0;
        return leftDue - rightDue;
      })
  ), [filtered]);

  const metrics = useMemo(() => {
    const now = new Date();
    const totalActions = filtered.length;
    
    // Status
    const done = filtered.filter(a => a.status === 'done').length;
    const doing = filtered.filter(a => a.status === 'doing').length;
    const toDo = filtered.filter(a => a.status === 'todo').length;
    const canceled = filtered.filter(a => a.status === 'canceled').length;
    
    // Prazos
    const withDeadline = filtered.filter(a => a.deadline);
    const overdue = filtered.filter(a => 
      a.deadline && 
      new Date(a.deadline) < now && 
      a.status !== 'done'
    ).length;
    const dueThisWeek = filtered.filter(a => {
      if (!a.deadline || a.status === 'done') return false;
      const deadline = new Date(a.deadline);
      const weekFromNow = new Date(now);
      weekFromNow.setDate(now.getDate() + 7);
      return deadline >= now && deadline <= weekFromNow;
    }).length;
    
    // Pessoas e equipe
    const uniqueResponsibles = new Set(
      filtered.map(a => a.responsavel || a.responsible).filter(Boolean)
    ).size;
    
    const responsibleWorkload = filtered.reduce((acc, a) => {
      const resp = a.responsavel || a.responsible || 'Não Atribuído';
      if (!acc[resp]) acc[resp] = 0;
      acc[resp]++;
      return acc;
    }, {} as Record<string, number>);
    
    const mostBusyPerson = Object.entries(responsibleWorkload)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0];
    
    // Impacto e esforço (escala 1/2/3)
    const avgImpact = filtered.reduce((sum, a) => sum + ((a as any).impacto || 0), 0) / totalActions || 0;
    const avgEffort = filtered.reduce((sum, a) => sum + ((a as any).esforco || 0), 0) / totalActions || 0;
    
    const quickWins = filtered.filter(a => 
      (a.impacto || 0) >= 3 && (a.esforco || 0) <= 1
    ).length;
    
    // Economia potencial
    const totalSavings = filtered.reduce((sum, a) => {
      const saving = a.cost ?? a.economy ?? a.economia_estimada ?? 0;
      return sum + (typeof saving === 'number' ? saving : 0);
    }, 0);
    
    const realizedSavings = filtered
      .filter(a => a.status === 'done')
      .reduce((sum, a) => {
        const saving = a.cost ?? a.economy ?? a.economia_estimada ?? 0;
        return sum + (typeof saving === 'number' ? saving : 0);
      }, 0);
    
    // Taxa de conclusão
    const completionRate = totalActions > 0 ? (done / totalActions) * 100 : 0;
    
    // Velocidade (ações concluídas nos últimos 30 dias)
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    const recentCompletions = filtered.filter(a => 
      a.status === 'done' && 
      a.completedAt && 
      new Date(a.completedAt) >= thirtyDaysAgo
    ).length;
    
    // Ciclo médio (tempo médio de conclusão)
    const completedWithDates = filtered.filter(a => 
      a.status === 'done' && 
      a.createdAt && 
      a.completedAt
    );
    const avgCycleTime = completedWithDates.length > 0
      ? completedWithDates.reduce((sum, a) => {
          const created = new Date(a.createdAt!).getTime();
          const completed = new Date(a.completedAt!).getTime();
          return sum + (completed - created) / (1000 * 60 * 60 * 24);
        }, 0) / completedWithDates.length
      : 0;
    
    return {
      totalActions,
      done,
      doing,
      toDo,
      canceled,
      overdue,
      dueThisWeek,
      uniqueResponsibles,
      mostBusyPerson,
      avgImpact,
      avgEffort,
      quickWins,
      totalSavings,
      realizedSavings,
      completionRate,
      recentCompletions,
      avgCycleTime,
      responsibleWorkload
    };
  }, [filtered]);

  // ============================================================================
  // GERAÇÃO DE INSIGHTS AUTOMÁTICOS
  // ============================================================================
  
  const insights = useMemo(() => {
    const result = [];
    
    // Insight 1: Ações atrasadas
    if (metrics.overdue > 0) {
      result.push({
        type: 'danger' as const,
        title: 'Atenção: Prazos Críticos!',
        message: `${metrics.overdue} ${metrics.overdue === 1 ? 'ação está' : 'ações estão'} atrasada${metrics.overdue === 1 ? '' : 's'}. Repriorize ou renegocie os prazos.`,
        icon: <AlertTriangle size={16} className="text-rose-600" />
      });
    }
    
    // Insight 2: Quick wins disponíveis
    if (metrics.quickWins > 0) {
      result.push({
        type: 'success' as const,
        title: 'Quick Wins Identificados!',
        message: `${metrics.quickWins} ${metrics.quickWins === 1 ? 'ação tem' : 'ações têm'} alto impacto e baixo esforço. Foque ${metrics.quickWins === 1 ? 'nela' : 'nelas'} para resultados rápidos.`,
        icon: <Zap size={16} className="text-emerald-600" />
      });
    }
    
    // Insight 3: Pessoa sobrecarregada
    if (metrics.mostBusyPerson && (metrics.mostBusyPerson[1] as number) > 10) {
      result.push({
        type: 'warning' as const,
        title: 'Sobrecarga Detectada',
        message: `${metrics.mostBusyPerson[0]} tem ${metrics.mostBusyPerson[1]} ações atribuídas. Considere redistribuir para equilibrar a carga.`,
        icon: <Users size={16} className="text-amber-600" />
      });
    }
    
    // Insight 4: Taxa de conclusão alta
    if (metrics.completionRate > 70) {
      result.push({
        type: 'success' as const,
        title: 'Execução Exemplar!',
        message: `${metrics.completionRate.toFixed(0)}% das ações foram concluídas. Continue com esse ritmo!`,
        icon: <ThumbsUp size={16} className="text-emerald-600" />
      });
    }
    
    // Insight 5: Ações canceladas
    if (metrics.canceled > 0) {
      result.push({
        type: 'warning' as const,
        title: 'Ações Canceladas',
        message: `${metrics.canceled} ${metrics.canceled === 1 ? 'ação foi cancelada' : 'ações foram canceladas'}.`,
        icon: <AlertTriangle size={16} className="text-amber-600" />
      });
    }
    
    // Insight 6: Vencimentos próximos
    if (metrics.dueThisWeek > 0) {
      result.push({
        type: 'info' as const,
        title: 'Ações com Prazo Próximo',
        message: `${metrics.dueThisWeek} ${metrics.dueThisWeek === 1 ? 'ação vence' : 'ações vencem'} esta semana. Planeje sua agenda.`,
        icon: <Calendar size={16} className="text-blue-600" />
      });
    }
    
    // Insight 7: Economia potencial
    if (metrics.totalSavings > 0) {
      result.push({
        type: 'info' as const,
        title: 'Potencial de Economia',
        message: `R$ ${metrics.totalSavings.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} em economia potencial. ${metrics.realizedSavings > 0 ? `R$ ${metrics.realizedSavings.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} estão em ações concluídas; isso não é benefício medido.` : ''}`,
        icon: <DollarSign size={16} className="text-blue-600" />
      });
    }
    
    return result;
  }, [metrics]);

  // ============================================================================
  // DADOS PARA GRÁFICOS
  // ============================================================================
  
  const statusData = useMemo(() => {
    // Compute runtime effective status: override todo/doing → 'Atrasada' when
    // the deadline has already passed and the action is not done/canceled.
    const now = new Date();
    let atrasada = 0;
    let emAndamento = 0;
    let aFazer = 0;
    filtered.forEach(a => {
      if (a.status === 'done' || a.status === 'canceled') return;
      const rawEnd = (a as any).deadline ?? (a as any).prazo;
      const endDate = rawEnd ? new Date(rawEnd) : null;
      if (endDate && endDate < now) {
        atrasada++;
      } else if (a.status === 'doing') {
        emAndamento++;
      } else {
        aFazer++;
      }
    });
    return {
      'Concluída':    metrics.done,
      'Em Andamento': emAndamento,
      'A Fazer':      aFazer,
      'Atrasada':     atrasada,
      'Cancelada':    metrics.canceled,
    };
  }, [filtered, metrics.done, metrics.canceled]);

  // Map 3-level enum (1=low,2=medium,3=high) to 0-100 coordinates for the scatter plot.
  // Levels → axis positions: 1→20, 2→50, 3→80. Unknown → 50 (neutral centre).
  // Small deterministic jitter per index prevents points from stacking perfectly.
  const LEVEL_POS: Record<number, number> = { 1: 20, 2: 50, 3: 80 };
  const impactEffortData = useMemo(() =>
    filtered.map((a, i) => {
      const jx = ((i * 17) % 7) - 3;  // –3 to +3
      const jy = ((i * 13) % 7) - 3;
      const x = (LEVEL_POS[Number(a.esforco) as 1|2|3] ?? 50) + jx;
      const y = (LEVEL_POS[Number(a.impacto) as 1|2|3] ?? 50) + jy;
      return { x: Math.max(2, Math.min(98, x)), y: Math.max(2, Math.min(98, y)), label: a.title };
    })
  , [filtered]);

  return (
    <div className="enterprise-page">
      {/* Header */}
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center border border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
              <BarChart2 size={18} className="text-slate-600 dark:text-slate-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Dashboard do Plano de Ação</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Visão executiva, KPIs e insights estratégicos</p>
            </div>
          </div>
          {organization && (
            <div className="text-right">
              <div className="text-[12px] font-medium text-slate-700 dark:text-slate-300">{organization.name}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400">{projects.length} projetos ativos</div>
            </div>
          )}
        </div>
      </div>

      {/* KPIs Principais - Grid 4 colunas */}
      <div className="mb-3 grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total de Ações"
          value={metrics.totalActions}
          subtitle={`${metrics.done} concluídas · ${metrics.completionRate.toFixed(0)}%`}
          icon={<Activity size={20} />}
          colorClass="border border-slate-300 bg-slate-100 text-slate-600"
        />
        
        <KPICard
          title="Em Andamento"
          value={metrics.doing}
          subtitle={`${metrics.toDo} aguardando início`}
          icon={<PlayCircle size={20} />}
          colorClass="border border-slate-300 bg-slate-100 text-slate-600"
        />
        
        <KPICard
          title="Prazos Críticos"
          value={metrics.overdue}
          subtitle={`${metrics.dueThisWeek} vencem esta semana`}
          icon={<Clock size={20} />}
          colorClass="border border-slate-300 bg-slate-100 text-slate-600"
          trend={metrics.overdue > 0 ? 'down' : 'neutral'}
        />
        
        <KPICard
          title="Equipe Ativa"
          value={metrics.uniqueResponsibles}
          subtitle={metrics.mostBusyPerson
            ? (metrics.mostBusyPerson[0].length > 28
              ? `${metrics.mostBusyPerson[0].slice(0, 28)}…`
              : metrics.mostBusyPerson[0])
            : 'Ninguém atribuído'}
          icon={<Users size={20} />}
          colorClass="border border-slate-300 bg-slate-100 text-slate-600"
        />
      </div>

      {lateActions.length > 0 && (
        <div className="mb-3 border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700">
            Atrasadas
          </div>
          <ul>
            {lateActions.map((action) => {
              const due = parseActionDeadline(action.prazo) ?? parseActionDeadline(action.deadline);
              const dueLabel = due
                ? due.toLocaleDateString('pt-BR')
                : 'Sem prazo';
              const owner = (action.responsavel || action.responsible || '').trim() || 'Sem responsável';
              return (
                <li key={action.id} className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0 dark:border-slate-800">
                  <span className="min-w-0 truncate text-[13px] font-medium text-slate-800 dark:text-slate-100">{action.title}</span>
                  <span className="shrink-0 text-[11px] text-slate-500">{owner}</span>
                  <span className="shrink-0 text-[11px] font-medium tabular-nums text-red-600">{dueLabel}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mb-3 grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-4">
        {metrics.quickWins > 0 && (
          <KPICard
            title="Quick Wins"
            value={metrics.quickWins}
            subtitle="Alto impacto, baixo esforço"
            icon={<Zap size={20} />}
            colorClass="border border-slate-300 bg-slate-100 text-slate-600"
          />
        )}
        <KPICard
          title="Velocidade (30d)"
          value={metrics.recentCompletions}
          subtitle="ações concluídas"
          icon={<Gauge size={20} />}
          colorClass="border border-slate-300 bg-slate-100 text-slate-600"
        />
        {metrics.avgCycleTime > 0 && (
          <KPICard
            title="Ciclo Médio"
            value={`${metrics.avgCycleTime.toFixed(0)}d`}
            subtitle="tempo até conclusão"
            icon={<Timer size={20} />}
            colorClass="border border-slate-300 bg-slate-100 text-slate-600"
          />
        )}
        {metrics.totalSavings > 0 && (
          <KPICard
            title="Economia Total"
            value={`R$ ${(metrics.totalSavings / 1000).toFixed(0)}k`}
            subtitle={`R$ ${(metrics.realizedSavings / 1000).toFixed(0)}k em ações concluídas`}
            icon={<DollarSign size={20} />}
            colorClass="border border-slate-300 bg-slate-100 text-slate-600"
          />
        )}
      </div>

      {/* Insights Automáticos */}
      {insights.length > 0 && (
        <div className="mb-4">
          <div className="mb-2.5 flex items-center gap-2">
            <Lightbulb size={16} className="text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Insights Estratégicos</h3>
          </div>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {insights.map((insight, i) => (
              <InsightCard key={i} {...insight} />
            ))}
          </div>
        </div>
      )}

      {/* Gráficos e Análises */}
      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Status das Ações */}
        <div className="enterprise-card-surface border border-enterprise-border-soft p-3">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PieChart size={16} className="text-slate-600 dark:text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Status das Ações</h3>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Distribuição atual</div>
          </div>
          <SimpleBarChart data={statusData} />
        </div>

        {/* Matriz Impacto x Esforço */}
        <div className="enterprise-card-surface border border-enterprise-border-soft p-3">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-slate-600 dark:text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Matriz Impacto x Esforço</h3>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Priorização</div>
          </div>
          <ScatterPlot points={impactEffortData} />
        </div>
      </div>

      {/* Carga por Responsável */}
      {Object.keys(metrics.responsibleWorkload).length > 0 && (
        <div className="enterprise-card-surface mb-3 border border-enterprise-border-soft p-3">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-slate-600 dark:text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Carga por Responsável</h3>
            </div>
            <div className="text-xs text-slate-500">Distribuição de trabalho</div>
          </div>
          <div className="space-y-2">
            {Object.entries(metrics.responsibleWorkload)
              .sort(([,a], [,b]) => (b as number) - (a as number))
              .slice(0, 10)
              .map(([name, count]) => {
                const percentage = ((count as number) / metrics.totalActions) * 100;
                return (
                  <div key={name} className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium text-slate-700">{name}</span>
                      <span className="text-slate-500">{count as number} {(count as number) === 1 ? 'ação' : 'ações'}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden bg-slate-200">
                      <div 
                        className="h-full bg-slate-600"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Gantt / Roadmap */}
      <div className="enterprise-card-surface border border-enterprise-border-soft p-3">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-600 dark:text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Roadmap / Gantt</h3>
          </div>
          <div className="text-xs text-slate-500">{filtered.length} ações no timeline</div>
        </div>
        <GanttView actions={filtered} />
      </div>
    </div>
  );
};

export default ActionDashboard;
